import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    InputLabel,
    OutlinedInput,
    Popper,
    Stack,
    styled,
    Switch,
    Typography,
    useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FullTaskTopbar from "../../../../Components/Topbars/FullTaskTopbar";
import FullTaskLayout from "../../../../Layouts/FullTask";
import FullTaskSplit from "../../../../Layouts/FullTask/FullTaskSplit";

import { useLocation, useNavigate, useParams } from "react-router";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import { AssetTypeAlphaColor, Distinct, GroupByToMap } from "../../../../Utils/helpers";

import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { GridColDef } from "@mui/x-data-grid-premium";
import moment from "moment";
import AssetIcon from "../../../../Components/Misc/AssetIcon/asset-icon";
import AssetTypeChip from "../../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import { ModalContext } from "../../../../Components/Modals";
import { IngestConfigurePlaceholderContext } from "../../../../Components/Modals/ModalCreateOrUpdatePlaceholder/modal-ingest-config-placeholder-create-or-update";
import ModalFindPlaceholder, {
    ModalFindPlaceholderCloseProps,
    ModalFindPlaceholderProps,
} from "../../../../Components/Modals/ModalFindPlaceholder/modal-ingest-config-placeholder-find";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import { ingestService, setupService } from "../../../../Core/Api/services";
import { DbFormatInfos } from "../../../../Core/Api/Types/db-format-infos";
import { DbIngestConfigPackInfos } from "../../../../Core/Api/Types/db-ingest-config-pack-infos";
import { DbIngestConfigPackPlaceholderRowFieldInfos } from "../../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-field-infos";
import { DbIngestConfigPackPlaceholderRowInfos } from "../../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-infos";
import { IngestConfigPackColumnSet } from "../../../../Core/Api/Types/ingest-config-pack-column-set";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { routePaths } from "../../../../Router/router";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { GetPlaceholderResponse } from "../../../../Core/Api/Types/get-placeholder-response";
import { useTranslation } from "react-i18next";
import { ExpandMoreOutlined } from "@mui/icons-material";

enum FieldHandleChange {
    OverrideIsNotMandatory,
    IsApplicable,
    Values,
}

export type IngestConfigurePlaceholderProps = {
    context: IngestConfigurePlaceholderContext;
};

// Class override for simplify construction: contains backend data about column and object to fill to send to the backend
export type IngestPlaeholderConfigBackend = {
    columnBackend: IngestConfigPackColumnSet;
    row: DbIngestConfigPackPlaceholderRowFieldInfos;
};

export type IngestColumnsGrouped = {
    group: string | null | undefined;
    columns: IngestPlaeholderConfigBackend[];
};

const IngestConfigurePlaceholder: React.FC<IngestConfigurePlaceholderProps> = (props) => {
    const { t } = useTranslation();
    const { context } = props;
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const enqueueCustomSnackbar = useCustomSnackbar();
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };
    const theme = useTheme();

    const [pack, setPack] = useState<DbIngestConfigPackInfos>();
    const [objectType, setObjectType] = useState<ObjectType>(ObjectType.Unknown);
    const [objectClasses, setObjectClasses] = useState<ObjectClass[]>([]);
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [pageTitle, setPageTitle] = useState("");
    const [placeholderId, setPlaceholderId] = useState(-1);
    const [optional, setOptional] = useState(false);
    const [columnsGrouped, setColumnsGrouped] = useState<IngestColumnsGrouped[]>();
    const [isLoading, setIsLoading] = useState(true);
    const [placeholderFormatsIds, setPlaceholderFormatsIds] = useState<number[]>();
    const [fetchedFormats, setFetchedFormats] = useState<DbFormatInfos[]>([]);
    const [selectedFomats, setSelectedFormats] = useState<DbFormatInfos[]>([]);

    // Meta provider must be always applicable and not overrided not mandatory
    const providerMetaTagName = "labProviderId";

    const handleValueChange = (event: any, value: any, fieldId: number, type: FieldHandleChange) => {
        if (!columnsGrouped) return;
        let newValue = [...columnsGrouped];

        newValue?.forEach((group) => {
            group.columns.forEach((col) => {
                if (col.columnBackend!.columnFieldInfos!.id! !== fieldId) return;

                switch (type) {
                    case FieldHandleChange.Values:
                        col.row.predefinedValues = value.map((v) => v);
                        break;
                    case FieldHandleChange.IsApplicable:
                        col.row.isApplicable = value;
                        break;
                    case FieldHandleChange.OverrideIsNotMandatory:
                        col.row.overrideIsNotMandatory = value;
                        break;
                }
            });
        });

        setColumnsGrouped(newValue);
    };

    const columnsAction: GridColDef[] = [
        {
            field: "fieldName",
            headerName: t("common:field"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            valueGetter: (value: any, row: any) => row?.columnBackend.columnInfos.label,
        },
        {
            field: "mandatory",
            headerName: t("common:mandatory"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            valueGetter: (value: any, row: any) => row?.columnBackend.columnInfos.isMandatory,
            renderCell: (data: any) => <TableCellBool value={data?.row?.columnBackend.columnInfos.isMandatory} />,
        },
        {
            field: "overrideIsNotMandatory",
            headerName: t("table:headerName.overrideNotMandatory"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            valueGetter: (value: any, row: any) => row?.row.overrideIsNotMandatory,
            renderCell: (data: any) => (
                <Switch
                    size="small"
                    value={data.value}
                    checked={data.value}
                    disabled={data?.row?.row.metadataField.tagName === providerMetaTagName}
                    onChange={(e, value) =>
                        handleValueChange(
                            e,
                            value,
                            data?.row?.columnBackend.columnFieldInfos.id,
                            FieldHandleChange.OverrideIsNotMandatory
                        )
                    }
                />
            ),
        },
        {
            field: "applicable",
            headerName: t("common:applicable"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            valueGetter: (value: any, row: any) => row?.row.isApplicable,
            renderCell: (data: any) => (
                <Switch
                    size="small"
                    value={data.value}
                    checked={data.value}
                    disabled={data?.row?.row.metadataField.tagName === providerMetaTagName}
                    onChange={(e, value) =>
                        handleValueChange(e, value, data?.row?.columnBackend.columnFieldInfos.id, FieldHandleChange.IsApplicable)
                    }
                />
            ),
        },
        {
            field: "values",
            headerName: t("table:headerName.restrictedValues"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            valueGetter: (value: any, row: any) => row?.row.predefinedValues,
            renderCell: (data: any) =>
                data?.row?.columnBackend.columnFieldInfos.values !== null && (
                    <Stack flexGrow={1} pr={2}>
                        <Autocomplete
                            id="autocomplete-small"
                            size="medium"
                            options={data?.row?.columnBackend.columnFieldInfos.values.filter(Distinct)}
                            getOptionLabel={(option: any) => option.label}
                            multiple
                            sx={{ flex: 1, my: 1 }}
                            value={data?.row?.columnBackend.columnFieldInfos.values.filter((x) =>
                                data?.row?.row.predefinedValues.includes(x.value)
                            )}
                            onChange={(e, value) =>
                                handleValueChange(
                                    e,
                                    value.map((v) => v.value),
                                    data?.row?.columnBackend.columnFieldInfos.id,
                                    FieldHandleChange.Values
                                )
                            }
                            PopperComponent={(props) => <Popper {...props} placement="bottom" />}
                            popupIcon={<ExpandMoreOutlined />}
                            renderInput={(params) => (
                                <>
                                    <OutlinedInput
                                        {...params.InputProps}
                                        inputProps={params.inputProps}
                                        placeholder={t("field:placeholder.restrictedValues")}
                                        sx={{ width: "100%" }}
                                    />
                                </>
                            )}
                        />
                    </Stack>
                ),
        },
    ];

    const handleConfirm = () => {
        // Construt final object to pass to backend
        const fields: DbIngestConfigPackPlaceholderRowFieldInfos[] = [];

        columnsGrouped?.forEach((group) => {
            group.columns.forEach((col) => {
                fields.push(col.row);
            });
        });

        const request: DbIngestConfigPackPlaceholderRowInfos = {
            isOptionnal: optional,
            packId: pack?.id!,
            objectType: objectType,
            lastUpdate: moment().toDate(),
            name: name,
            description: desc,
            objectClasses: objectClasses,
            fields: fields,
            uploadableFileFormatIds: selectedFomats.length > 0 ? selectedFomats.map((format) => format.id) : null, // TODO
            isSelected: false,
            id: placeholderId,
            isNew: context === IngestConfigurePlaceholderContext.Create,
        };

        // Clean this to avoid any backend issue
        request.fields?.forEach((x) => {
            x.metadataField = null;
        });

        switch (context) {
            case IngestConfigurePlaceholderContext.Create:
            case IngestConfigurePlaceholderContext.Duplicate:
                ingestService.CreatePlaceholder(pack?.id!, request).then((response) => {
                    if (response.success) {
                        navigate(`${routePaths.ingestPacks.path}/${pack?.id}`);
                        let str = context === IngestConfigurePlaceholderContext.Create ? "created" : "duplicated";
                        enqueueCustomSnackbar(t("alert:success.placeholderSuccess", { context: str }), "success");
                    } else {
                        enqueueCustomSnackbar(`${response.message}`, "error");
                    }
                });
                break;
            case IngestConfigurePlaceholderContext.Update:
                ingestService.UpdatePlaceholder(request).then((response) => {
                    if (response.success) {
                        navigate(`${routePaths.ingestPacks.path}/${pack?.id}`);
                        enqueueCustomSnackbar(t("alert:success.placeholderSuccess", { context: "updated" }), "success");
                    } else {
                        enqueueCustomSnackbar(`${response.message}`, "error");
                    }
                });
                break;
        }
    };

    const handleImportConfig = (placeholderId: number) => {
        if (!columnsGrouped) return;

        setIsLoading(true);

        ingestService.GetPlaceholderById(placeholderId).then((response) => {
            // Make same object's list as "New" context
            let columnsArray: IngestColumnsGrouped[] = columnsGrouped!;

            columnsArray.forEach((group) => {
                group.columns.forEach((col) => {
                    let fieldId: number = col.columnBackend!.columnFieldInfos!.id!;
                    let matchedField = response.placeholder?.fields?.find((x) => x.metadataField?.id === fieldId);

                    if (!matchedField) return;

                    if (matchedField.predefinedValues === null) matchedField.predefinedValues = [];

                    col.row = matchedField;
                });
            });

            setColumnsGrouped(columnsArray);

            setIsLoading(false);
            enqueueCustomSnackbar(t("alert:success.placeholderConfigurationImported"), "success");
        });
    };

    const onClickOpenModalImportConfig = () => {
        if (!openModal) return;

        openModal<ModalFindPlaceholderProps>(ModalFindPlaceholder, {
            packId: pack?.id!,
            objectType: objectType,
            closeModal: () => false,
            onClose: (closeProps: ModalFindPlaceholderCloseProps) =>
                closeProps.id ? handleImportConfig(closeProps.id) : false,
        });
    };

    useEffect(() => {
        // Store pack and choices from previous modal using props with navigate location
        if (!location.state || location.state === null) {
            navigate(routePaths.ingestPacks.path);
            return;
        }

        setPack(location.state.pack);
        setObjectType(location.state?.objectType);
        setObjectClasses(location.state?.objectClasses);
        setName(location.state?.name);
        setDesc(location.state?.desc);
        setOptional(location.state?.isOptional);

        // Load columns set
        ingestService.GetIngestMetadataFields().then((response) => {
            if (!response.columns) return;

            // Group metadata by "group" prop
            let grouped = GroupByToMap(response.columns!, (x) => x.columnFieldInfos?.group);
            let columnsArray: IngestColumnsGrouped[] = [];

            columnsArray = createColumns(grouped);

            // Depending of context, set pack infos
            switch (context) {
                case IngestConfigurePlaceholderContext.Create:
                    setColumnsGrouped(columnsArray);

                    setPageTitle(t("page:title.createPlaceholderInLocation", { location: location.state.pack?.name }));

                    setIsLoading(false);
                    break;
                case IngestConfigurePlaceholderContext.Update:
                case IngestConfigurePlaceholderContext.Duplicate:
                    if (!params.id) {
                        navigate(routePaths.ingestPacks.path);
                        return;
                    }

                    let pid = parseInt(params.id);
                    setPlaceholderId(pid);

                    ingestService.GetPlaceholderById(pid).then((response) => {
                        setPageTitle(
                            `${IngestConfigurePlaceholderContext[context]} ${t("common:placeholder")} ${response.placeholder?.name!}`
                        );

                        // Make same object's list as "New" context
                        handleMatchColumns(columnsArray, response);

                        setColumnsGrouped(columnsArray);

                        setIsLoading(false);
                        setPlaceholderFormatsIds(response.placeholder?.uploadableFileFormatIds || []);
                    });
                    break;
            }
        });

        setupService.GetFormats().then((response) => {
            if (response.formats) setFetchedFormats(response.formats.sort((a, b) => a.name!.localeCompare(b.name!)));
        });
    }, [params.id, placeholderId]);

    useEffect(() => {
        if (fetchedFormats.length > 0 && placeholderFormatsIds)
            setSelectedFormats(fetchedFormats.filter((f) => placeholderFormatsIds.includes(f.id)));
    }, [placeholderFormatsIds, fetchedFormats]);

    useEffect(() => {
        if (columnsGrouped) {
            let providerField = columnsGrouped
                ?.find((x) => x.group === null)
                ?.columns.find((x) => x.row.metadataField?.tagName === providerMetaTagName);

            if (providerField && providerField?.row.overrideIsNotMandatory === true)
                handleValueChange(null, false, providerField.row.id, FieldHandleChange.OverrideIsNotMandatory);

            if (providerField && providerField?.row.isApplicable === false)
                handleValueChange(null, true, providerField.row.id, FieldHandleChange.IsApplicable);
        }
    }, [columnsGrouped]);

    return (
        <FullTaskLayout
            TopBar={
                <FullTaskTopbar
                    title={pageTitle}
                    backRoute={`${routePaths.ingestPacks.path}/${pack?.id}`}
                    rightContent={
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {IngestConfigurePlaceholderContext[context]} {t("common:placeholder")}
                        </Button>
                    }
                />
            }
            sxWrapper={{ height: "100vh" }}
            sxStack={{ width: "100%" }}
        >
            <FullTaskSplit
                layout="1:4"
                sx={{
                    p: 3,
                    background: `${theme.palette.background.paper} !important`,
                    "&:before": {
                        content: '""',
                        position: "absolute",
                        width: "30%",
                        height: "80%",
                        left: "-5%",
                        top: "-5%",
                        background:
                            theme.palette.mode === "dark"
                                ? AssetTypeAlphaColor(objectType, 0.1)
                                : AssetTypeAlphaColor(objectType, 0.15),
                        filter: " blur(106px)",
                        transform: "rotate(20.23deg)",
                        pointerEvents: "none",
                    },
                }}
                sxContainer={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Stack alignItems="center" justifyContent="center" position="relative" width={220} zIndex={1}>
                    <StyledBox assetType={objectType}>
                        <AssetTypeChip type={objectType} />
                        <AssetIcon type={objectType} sx={{ fontSize: 98, color: AssetTypeAlphaColor(objectType, 1) }} />
                    </StyledBox>
                    <Typography mb={1} mt={3} variant="subtitle2" textAlign="center">
                        {name}
                    </Typography>
                    <Stack direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" spacing={0.5}>
                        {objectClasses?.map((c, index) => (
                            <Chip label={c} key={index} />
                        ))}
                    </Stack>
                </Stack>
            </FullTaskSplit>
            <FullTaskSplit layout="1:4">
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={3}>
                        <Stack>
                            <Typography variant="subtitle2" component="h2">
                                {t("common:configuration")}
                            </Typography>
                            <Typography color="text.secondary" variant="body2" mt={0.5}>
                                {t("page:body.chooseValuesToAddToPlaceholder")}
                            </Typography>
                        </Stack>
                        <Button startIcon={<FileUploadOutlinedIcon />} onClick={onClickOpenModalImportConfig}>
                            {t("button:importFromExistingPlaceholder")}
                        </Button>
                    </Stack>

                    {isLoading && (
                        <CircularProgress
                            sx={{
                                position: "relative",
                                top: "44%",
                                left: "50%",
                            }}
                        />
                    )}
                    {!isLoading && (
                        <>
                            {columnsGrouped?.map((value, index) => (
                                <FormSection
                                    size="small"
                                    title={value.group ? value.group : t("common:general")}
                                    status="todo"
                                    withContainer={false}
                                    defaultExpanded
                                    icon={<SettingsOutlinedIcon />}
                                    sx={{ mb: 3 }}
                                    key={`fsection-${index}`}
                                >
                                    <Stack px={2}>
                                        <DataTable
                                            noControls
                                            rows={value.columns}
                                            columns={columnsAction}
                                            getRowHeight={() => "auto"}
                                            getRowId={(row: any) => row.columnBackend.columnFieldInfos.id}
                                        />
                                    </Stack>
                                </FormSection>
                            ))}
                            <Stack sx={{ mb: 10, pb: 5 }} spacing={1}>
                                <InputLabel>{t("field:label.enabledFormats")}</InputLabel>
                                <Autocomplete
                                    multiple
                                    options={fetchedFormats}
                                    getOptionLabel={(option) => option.name || "N/A"}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={selectedFomats}
                                    onChange={(_, value) => setSelectedFormats(value)}
                                    renderInput={(params) => (
                                        <OutlinedInput
                                            {...params.InputProps}
                                            inputProps={params.inputProps}
                                            required
                                            sx={{ width: "100%" }}
                                        />
                                    )}
                                    popupIcon={<ExpandMoreRoundedIcon />}
                                />
                            </Stack>
                        </>
                    )}
                </>
            </FullTaskSplit>
        </FullTaskLayout>
    );
};

export default IngestConfigurePlaceholder;

const StyledBox = styled(Box, { shouldForwardProp: (prop) => prop !== "assetType" })<{ assetType: ObjectType }>(
    ({ assetType, theme }) => ({
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 12,
        width: 220,
        height: 220,
        background: AssetTypeAlphaColor(assetType, 0.1),
        overflow: "hidden",
        boxShadow: theme.shadows[4],
        "&:before, &:after": {
            position: "absolute",
            content: '""',
            filter: " blur(84px)",
        },
        "&:before": {
            width: 212,
            height: 212,
            left: -130,
            top: 94,
            background: theme.palette.background.paper,
            filter: " blur(84px)",
        },
        "&:after": {
            width: 170,
            height: 170,
            left: 120,
            top: -95,
            background:
                theme.palette.mode === "dark" ? AssetTypeAlphaColor(assetType, 0.1) : AssetTypeAlphaColor(assetType, 0.3),
        },

        ".MuiChip-root": {
            position: "absolute",
            top: 12,
            left: 12,
        },
    })
);

export function convertFieldToRow(column: IngestConfigPackColumnSet) {
    let row: DbIngestConfigPackPlaceholderRowFieldInfos = {
        matchByMetadataFieldValues: false,
        isApplicable: false,
        overrideIsNotMandatory: false,
        columnMetadataFieldId: column.columnFieldInfos?.id!,
        predefinedValues: [],
        metadataField: column.columnFieldInfos,
        matchByValues: false,
        isSystem: false,
        id: column.columnFieldInfos?.id!,
        isNew: false,
    };

    return row;
}

export function createColumns(grouped: any) {
    let columnsArray: IngestColumnsGrouped[] = [];

    grouped?.forEach((value: IngestConfigPackColumnSet[], key: string | null | undefined) => {
        columnsArray.push({
            group: key,
            // Take only real fields and not system ones (like type etc.)
            columns: value
                .filter((x) => x.columnFieldInfos?.id! > 0)
                .map((v) => ({ columnBackend: v, row: convertFieldToRow(v) })),
        });
    });

    return columnsArray;
}

export function handleMatchColumns(columnsArray: IngestColumnsGrouped[], response: GetPlaceholderResponse) {
    columnsArray.forEach((group) => {
        group.columns.forEach((col) => {
            let fieldId: number = col.columnBackend!.columnFieldInfos!.id!;
            let matchedField = response.placeholder?.fields?.find((x) => x.metadataField?.id === fieldId);

            if (!matchedField) return;

            if (matchedField.predefinedValues === null) matchedField.predefinedValues = [];

            col.row = matchedField;
        });
    });
}
